// Utility to fetch and filter reports from Azure Blob Storage
// Reports are in format: <client_name><plantype><date>.pdf

import { getAccountName, getContainer, getSASToken } from "./azureAttachments";
import { apiGet } from "../api/api_methods";

/**
 * Fetch reports via backend (role-aware: agents see assigned clients only).
 */
function mapApiReportsWithSas(data, sasToken) {
  return data.map((report) => {
    const baseUrl = report.url ? String(report.url).split("?")[0] : "";
    return {
      ...report,
      blobName: report.blobName || report.name,
      downloadUrl: baseUrl ? `${baseUrl}?${sasToken}` : report.downloadUrl,
      lastModified: report.lastModified ? new Date(report.lastModified) : null,
    };
  });
}

export async function fetchReportsFromApi() {
  const response = await apiGet("/adminPanel/reports");

  if (response.status === 404 || response.status === 502) {
    console.warn(
      "Reports API unavailable; falling back to direct Azure listing."
    );
    return fetchReportsFromAzure();
  }

  if (response.status !== 200 || !response.body?.success) {
    const message =
      response.body?.message ||
      `Failed to fetch reports (status ${response.status})`;
    if (response.status >= 500) {
      console.warn(`${message} — trying Azure fallback.`);
      return fetchReportsFromAzure();
    }
    throw new Error(message);
  }

  const sasToken = await getSASToken();
  const data = Array.isArray(response.body.data) ? response.body.data : [];

  return mapApiReportsWithSas(data, sasToken);
}

/**
 * Fetch all PDF reports from Azure Blob Storage
 * Reports follow the pattern: <client_name><plantype><date>.pdf
 * @returns {Promise<Array>} Array of report objects with name, url, and metadata
 */
export async function fetchReportsFromAzure() {
  try {
    const [accountName, container, sasToken] = await Promise.all([
      getAccountName(),
      getContainer(),
      getSASToken(),
    ]);

    // Azure Blob List API endpoint
    const listBlobsUrl = `https://${accountName}.blob.core.windows.net/${container}?restype=container&comp=list&${sasToken}`;

    const response = await fetch(listBlobsUrl, {
      method: "GET",
      headers: {
        "x-ms-version": "2020-10-02",
      },
    });

    if (!response.ok) {
      console.error("Failed to fetch blobs from Azure:", response.status);
      throw new Error(`Failed to fetch reports from Azure (status ${response.status})`);
    }

    const xmlText = await response.text();
    
    // Parse XML response
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, "text/xml");
    
    // Get all blob elements
    const blobs = xmlDoc.getElementsByTagName("Blob");
    const reports = [];

      for (let i = 0; i < blobs.length; i++) {
      const blob = blobs[i];
      const nameElement = blob.getElementsByTagName("Name")[0];
      const lastModifiedElement = blob.getElementsByTagName("Last-Modified")[0];
      const sizeElement = blob.getElementsByTagName("Content-Length")[0];
      
      if (nameElement) {
        const blobName = nameElement.textContent;
        
        // Filter only PDF files that match the report pattern
        // Pattern: ends with .pdf and contains report data (not Invoice or Test files)
        if (blobName.toLowerCase().endsWith(".pdf")) {
          const fileName = blobName.split("/").pop(); // Get filename without path
          
          // Skip Invoice and Test files - only process actual reports
          if (fileName.toLowerCase().includes("invoice") || 
              fileName.toLowerCase().includes("_test.pdf") ||
              fileName.toLowerCase().includes("_test_")) {
            continue; // Skip this file
          }
          
          // Extract metadata from filename
          const metadata = parseReportFilename(fileName);
          
          // Only include files that have valid metadata (are actual reports)
          if (metadata) {
            // URL-encode the blob name properly - encode each path segment separately
            // to handle special characters like colons in timestamps
            const encodedBlobName = blobName
              .split('/')
              .map(segment => encodeURIComponent(segment))
              .join('/');
            const blobUrl = `https://${accountName}.blob.core.windows.net/${container}/${encodedBlobName}`;
            
            reports.push({
              name: fileName,
              blobName: blobName,
              url: blobUrl,
              downloadUrl: `${blobUrl}?${sasToken}`,
              lastModified: lastModifiedElement ? new Date(lastModifiedElement.textContent) : null,
              size: sizeElement ? parseInt(sizeElement.textContent) : 0,
              ...metadata,
            });
          }
        }
      }
    }

    // Sort by last modified date (newest first)
    reports.sort((a, b) => {
      if (!a.lastModified) return 1;
      if (!b.lastModified) return -1;
      return b.lastModified - a.lastModified;
    });

    return reports;
  } catch (error) {
    console.error("Error fetching reports from Azure:", error);
    throw error;
  }
}

/**
 * Parse report filename to extract metadata
 * Supports multiple Azure formats:
 * 1. <timestamp>_<client_name>_<plantype>_<date>.pdf (e.g., 2026-01-06T06-47-54-791Z_ajay_chaudahry_comprehensive_06-01-2026.pdf)
 * 2. <client_name>_<plantype>_<date>_<time>.pdf (e.g., Bhoutik_Bhuria_comprehensive_23-12-2025_13:29:21.pdf)
 * 3. <client_name>_<plantype>_<date>.pdf (e.g., John_Doe_basic_15-01-2026.pdf)
 * @param {string} filename - The report filename
 * @returns {Object|null} Parsed metadata or null if doesn't match pattern
 */
function parseReportFilename(filename) {
  try {
    // Remove .pdf extension
    const nameWithoutExt = filename.replace(/\.pdf$/i, "");
    
    // Split by underscores
    const parts = nameWithoutExt.split('_');
    
    // Need at least 3 parts for a valid report
    if (parts.length < 3) {
      return null;
    }
    
    // Valid plan types - only these are accepted (normalized to lowercase for comparison)
    const validPlanTypes = [
      'comprehensive',
      'basic',
      'wealthmanagement',
      'zero2one',
    ];
    
    // Helper function to check if a string is a valid plan type (case-insensitive)
    const isValidPlanType = (str) => {
      if (!str) return false;
      return validPlanTypes.includes(str.toLowerCase());
    };
    
    // Helper function to normalize plan type to consistent format
    const normalizePlanType = (str) => {
      if (!str) return str;
      const lower = str.toLowerCase();
      // Map to canonical format
      if (lower === 'wealthmanagement') return 'wealthManagement';
      if (lower === 'zero2one') return 'Zero2One';
      return lower; // comprehensive, basic stay lowercase
    };
    
    // Pattern 1: Starts with ISO timestamp (YYYY-MM-DDTHH-MM-SS)
    // Example: 2026-01-06T06-47-54-791Z_ajay_chaudahry_comprehensive_06-01-2026.pdf
    const timestampPattern = /^\d{4}-\d{2}-\d{2}T/;
    if (timestampPattern.test(parts[0])) {
      // Remove the timestamp part (first element)
      const withoutTimestamp = parts.slice(1);
      
      if (withoutTimestamp.length >= 3) {
        // Last part is the date
        const reportDate = withoutTimestamp[withoutTimestamp.length - 1];
        
        // Second to last is the plan type
        const planType = withoutTimestamp[withoutTimestamp.length - 2];
        
        // Validate plan type
        if (!isValidPlanType(planType)) {
          return null;
        }
        
        // Everything before that is the client name
        const clientName = withoutTimestamp.slice(0, withoutTimestamp.length - 2).join(' ');
        
        return {
          clientName: clientName || "Unknown",
          planType: normalizePlanType(planType) || "Unknown",
          reportDate: reportDate || "Unknown",
        };
      }
    }
    
    // Pattern 2 & 3: No ISO timestamp, starts with client name
    // Example: Bhoutik_Bhuria_comprehensive_23-12-2025_13:29:21.pdf
    // Example: John_Doe_basic_15-01-2026.pdf
    
    // Check if first part looks like a hash/ID (long alphanumeric string)
    // Skip these as they're not valid client reports
    if (parts[0].length > 20 && /^[a-f0-9]+$/.test(parts[0])) {
      return null; // This is likely a hash/ID, not a client name
    }
    
    // Find the date part (format: DD-MM-YYYY)
    const datePattern = /^\d{2}-\d{2}-\d{4}$/;
    let dateIndex = -1;
    
    for (let i = 0; i < parts.length; i++) {
      if (datePattern.test(parts[i])) {
        dateIndex = i;
        break;
      }
    }
    
    if (dateIndex >= 2) {
      // We found a date, and there are at least 2 parts before it (name and plan type)
      const reportDate = parts[dateIndex];
      const planType = parts[dateIndex - 1];
      
      // Validate plan type
      if (!isValidPlanType(planType)) {
        return null;
      }
      
      const clientName = parts.slice(0, dateIndex - 1).join(' ');
      
      return {
        clientName: clientName || "Unknown",
        planType: planType || "Unknown",
        reportDate: reportDate || "Unknown",
      };
    }
    
    // Pattern fallback: Try to parse if we have at least 3 parts
    // Assume: parts[0..n-2] = client name, parts[n-1] = plan type OR date
    if (parts.length >= 3) {
      // Check if last part is a date (with or without time)
      const lastPart = parts[parts.length - 1];
      const secondLastPart = parts[parts.length - 2];
      
      // If last part looks like a time (HH:MM:SS), second last should be date
      const timePattern = /^\d{2}:\d{2}:\d{2}$/;
      if (timePattern.test(lastPart) && datePattern.test(secondLastPart)) {
        // Format: <name>_<plantype>_<date>_<time>
        const reportDate = secondLastPart;
        const planType = parts[parts.length - 3];
        
        // Validate plan type
        if (!isValidPlanType(planType)) {
          return null;
        }
        
        const clientName = parts.slice(0, parts.length - 3).join(' ');
        
        return {
          clientName: clientName || "Unknown",
          planType: normalizePlanType(planType) || "Unknown",
          reportDate: reportDate || "Unknown",
        };
      }
      
      // If last part is a date
      if (datePattern.test(lastPart)) {
        // Format: <name>_<plantype>_<date>
        const reportDate = lastPart;
        const planType = secondLastPart;
        
        // Validate plan type
        if (!isValidPlanType(planType)) {
          return null;
        }
        
        const clientName = parts.slice(0, parts.length - 2).join(' ');
        
        return {
          clientName: clientName || "Unknown",
          planType: normalizePlanType(planType) || "Unknown",
          reportDate: reportDate || "Unknown",
        };
      }
    }
    
    // If no pattern matches, return null
    return null;
  } catch (error) {
    console.error("Error parsing report filename:", filename, error);
    return null;
  }
}

/**
 * Download a report file from Azure
 * @param {string} downloadUrl - The download URL with SAS token
 * @param {string} filename - The filename to save as
 */
export async function downloadReport(downloadUrl, filename) {
  try {
    // Create a temporary link and trigger download directly
    // This avoids CORS issues and is more reliable
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename;
    link.target = "_blank"; // Open in new tab as fallback
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error("Error downloading report:", error);
    throw error;
  }
}

/**
 * Filter reports by client name, plan type, or date
 * @param {Array} reports - Array of report objects
 * @param {string} searchTerm - Search term to filter by
 * @returns {Array} Filtered reports
 */
export function filterReports(reports, searchTerm) {
  if (!searchTerm || searchTerm.trim() === "") {
    return reports;
  }
  
  const search = searchTerm.toLowerCase().trim();
  
  return reports.filter((report) => {
    return (
      report.clientName?.toLowerCase().includes(search) ||
      report.planType?.toLowerCase().includes(search) ||
      report.reportDate?.toLowerCase().includes(search) ||
      report.name?.toLowerCase().includes(search)
    );
  });
}
